import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { Campaign, CampaignStatus } from '../../types/Campaign';
import campaignService from '../../services/CampaignService';

// Import the new phase-specific overview components
import PrepOverview from './Phases/Prep/PrepOverview';
import MatchingOverview from './Phases/Matching/MatchingOverview';
import RunningOverview from './Phases/Running/RunningOverview';
import ClosedOverview from './Phases/Closed/ClosedOverview';

const CampaignOverview = () => {
    const { campaign_id: campaignId, organization_id: organizationId } = useParams<{ campaign_id: string; organization_id: string }>();
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchCampaign = async () => {
            if (!campaignId || !organizationId) {
                setError('Campaign ID is missing.');
                setLoading(false);
                return;
            }
            try {
                const data = await campaignService.getCampaign(organizationId, campaignId);
                setCampaign(data);
                
                // Only redirect if we're at the base campaign path
                if (data.campaign_status === CampaignStatus.PREP && 
                    location.pathname === `/organizations/${organizationId}/campaigns/${campaignId}`) {
                    navigate(`/organizations/${organizationId}/campaigns/${campaignId}/prep/details`, { replace: true });
                }
            } catch (err) {
                setError('Failed to fetch campaign data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaign();
    }, [campaignId, organizationId, navigate, location.pathname]);

    const renderPhaseComponent = () => {


        switch (campaign?.campaign_status) {
            case CampaignStatus.PREP:
                return <PrepOverview />;
            case CampaignStatus.MATCHING:
                return <MatchingOverview />;
            case CampaignStatus.RUNNING:
                return <RunningOverview />;
            case CampaignStatus.CLOSED:
                return <ClosedOverview />;
            default:
                return <Typography>This campaign does not have dates set. Please set dates to see the overview.</Typography>;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!campaign) {
        return <Alert severity="warning">No campaign data found.</Alert>;
    }

    return (
        renderPhaseComponent()
    );
};

export default CampaignOverview;